"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { ArrowRight, CheckCircle2, Clock } from "lucide-react";
import { useTranslations } from "next-intl";

import { AppAssets } from "@/lib/assets";
import { CancelConfirmModal } from "@/components/ui/cancel-confirm-modal";

export type EnrollmentStep = "payment_pending" | "identity_pending" | "liveness_pending" | "complete";

export interface CurrentMembership {
  status: "active" | "pending";
  slug: string;
  tier_price: number;
  invoice_id: string | null;
  enrollment_step: EnrollmentStep;
}

export interface ApiTier {
  id: string;
  slug: string;
  name: string;
  tagline: string;
  pricePerSeason: number;
  highlighted: boolean;
  benefits: string[];
  crest: string;
  colorTheme?: string;
}

function formatRupiah(value: number): string {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(value);
}

type TierStyle = { bg: string; orb: string; accent: string; text: string; badgeBg: string; priceDivider: string; glow: string };

const THEME_STYLE: Record<string, TierStyle> = {
  navy:    { bg: "linear-gradient(140deg, #0D1130 0%, #1B3A7A 55%, #0a0d2e 100%)", orb: "#1B3A7A", accent: "#93c5fd", text: "#e0e7ff", badgeBg: "rgba(147,197,253,0.15)", priceDivider: "rgba(147,197,253,0.2)", glow: "0 0 48px rgba(27,58,122,0.4), 0 8px 32px rgba(0,0,0,0.5)" },
  copper:  { bg: "linear-gradient(140deg, #5c1e0a 0%, #8B3A1A 55%, #3d1005 100%)", orb: "#f59e0b", accent: "#fbbf24", text: "#fde68a", badgeBg: "rgba(251,191,36,0.18)", priceDivider: "rgba(251,191,36,0.2)", glow: "0 0 48px rgba(139,58,26,0.5), 0 8px 32px rgba(0,0,0,0.5)" },
  indigo:  { bg: "linear-gradient(140deg, #1a1040 0%, #3b1f8f 55%, #110a30 100%)", orb: "#818cf8", accent: "#a5b4fc", text: "#e0e7ff", badgeBg: "rgba(165,180,252,0.18)", priceDivider: "rgba(165,180,252,0.2)", glow: "0 0 48px rgba(59,31,143,0.5), 0 8px 32px rgba(0,0,0,0.5)" },
  forest:  { bg: "linear-gradient(140deg, #0a2614 0%, #145a2e 55%, #07190e 100%)", orb: "#4ade80", accent: "#86efac", text: "#dcfce7", badgeBg: "rgba(134,239,172,0.15)", priceDivider: "rgba(134,239,172,0.2)", glow: "0 0 48px rgba(20,90,46,0.5), 0 8px 32px rgba(0,0,0,0.5)" },
  crimson: { bg: "linear-gradient(140deg, #3d0c0c 0%, #8b1a1a 55%, #280808 100%)", orb: "#f87171", accent: "#fca5a5", text: "#fee2e2", badgeBg: "rgba(252,165,165,0.15)", priceDivider: "rgba(252,165,165,0.2)", glow: "0 0 48px rgba(139,26,26,0.5), 0 8px 32px rgba(0,0,0,0.5)" },
  amber:   { bg: "linear-gradient(140deg, #451a03 0%, #92400e 55%, #2e1002 100%)", orb: "#f59e0b", accent: "#fbbf24", text: "#fde68a", badgeBg: "rgba(251,191,36,0.18)", priceDivider: "rgba(251,191,36,0.2)", glow: "0 0 48px rgba(146,64,14,0.5), 0 8px 32px rgba(0,0,0,0.5)" },
};

const FALLBACK_STYLE = THEME_STYLE.navy;

function getTierStyle(tier: ApiTier): TierStyle {
  if (tier.colorTheme && THEME_STYLE[tier.colorTheme]) return THEME_STYLE[tier.colorTheme];
  return FALLBACK_STYLE;
}

export function TierCarousel({
  tiers,
  hasPhone,
  isAuthenticated,
  currentMembership,
}: {
  tiers: ApiTier[];
  hasPhone: boolean;
  isAuthenticated: boolean;
  currentMembership?: CurrentMembership | null;
}) {
  const t = useTranslations("Membership");
  const router = useRouter();
  const [active, setActive] = useState(tiers.findIndex((m) => m.highlighted));
  const [cancelling, setCancelling] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const cards = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const index = cards.current.indexOf(entry.target as HTMLElement);
            if (index >= 0) setActive(index);
          }
        }
      },
      { root: scroller.current, threshold: 0.6 },
    );
    for (const card of cards.current) if (card) observer.observe(card);
    return () => observer.disconnect();
  }, []);

  const tier = tiers[active];
  const activeStyle = getTierStyle(tier);
  const [showCancelModal, setShowCancelModal] = useState(false);

  async function handleCancel() {
    setCancelling(true);
    try {
      await fetch("/api/borneox/membership/memberships/pending", { method: "DELETE" });
      router.refresh();
    } finally {
      setCancelling(false);
      setShowCancelModal(false);
    }
  }

  // Determine if selected tier is disabled (same or lower than active membership)
  const activeTierPrice = currentMembership?.tier_price ?? 0;
  const isDisabled = currentMembership?.status === "active" && tier.pricePerSeason <= activeTierPrice;
  const isCurrentTier = currentMembership?.slug === tier.slug;

  return (
    <>
      {/* Pending membership banner — hanya saat belum bayar */}
      {currentMembership?.status === "pending" && currentMembership.enrollment_step === "payment_pending" && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 space-y-3">
          <div className="flex items-start gap-3">
            <Clock className="size-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-amber-800 text-sm">Pembayaran menunggu konfirmasi</p>
              <p className="text-xs text-amber-700 mt-0.5">Anda memiliki transaksi membership yang belum dibayar.</p>
            </div>
          </div>
          <div className="flex gap-2">
            {currentMembership.invoice_id && (
              <button
                type="button"
                onClick={() => router.push(`/membership/instruksi/${currentMembership.invoice_id}`)}
                className="flex-1 rounded-xl bg-amber-600 px-3 py-2 text-sm font-bold text-white hover:bg-amber-700 transition-colors"
              >
                Lanjutkan Pembayaran
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowCancelModal(true)}
              disabled={cancelling}
              className="flex-1 rounded-xl border border-amber-300 bg-white px-3 py-2 text-sm font-bold text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-50"
            >
              {cancelling ? "Membatalkan…" : "Batalkan"}
            </button>
          </div>
        </div>
      )}

      {/* Bayar sudah, enrollment belum selesai */}
      {currentMembership != null && currentMembership.enrollment_step !== "complete" && currentMembership.enrollment_step !== "payment_pending" && (
        <div className="rounded-2xl border border-green-200 bg-green-50 p-4 space-y-3">
          <div className="flex items-start gap-3">
            <Clock className="size-5 text-green-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-green-800 text-sm">Pembayaran berhasil!</p>
              <p className="text-xs text-green-700 mt-0.5">
                {currentMembership.enrollment_step === "identity_pending"
                  ? "Lengkapi data identitas untuk mengaktifkan membership Anda."
                  : "Selesaikan verifikasi wajah untuk mengaktifkan membership Anda."}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => router.push(
              currentMembership.enrollment_step === "identity_pending"
                ? "/membership/identitas"
                : "/membership/liveness"
            )}
            className="w-full rounded-xl bg-green-600 px-3 py-2 text-sm font-bold text-white hover:bg-green-700 transition-colors"
          >
            {currentMembership.enrollment_step === "identity_pending"
              ? "Lengkapi Data Identitas"
              : "Mulai Verifikasi Wajah"}
          </button>
        </div>
      )}

      <CancelConfirmModal
        isOpen={showCancelModal}
        onConfirm={handleCancel}
        onClose={() => setShowCancelModal(false)}
        loading={cancelling}
      />

      {/* Active membership info */}
      {currentMembership?.status === "active" && (
        <div className="rounded-2xl border border-green-200 bg-green-50 p-3 flex items-center gap-3">
          <CheckCircle2 className="size-5 text-green-600 shrink-0" />
          <p className="text-sm text-green-800">
            Membership <span className="font-bold">{currentMembership.slug}</span> Anda aktif. Pilih tier lebih tinggi untuk upgrade.
          </p>
        </div>
      )}

      <div
        ref={scroller}
        className="-mx-container-margin flex snap-x snap-mandatory gap-4 overflow-x-auto px-container-margin pb-6"
        style={{ scrollbarWidth: "none" } as React.CSSProperties}
      >
        {tiers.map((m, i) => {
          const isActive = i === active;
          const s = getTierStyle(m);
          const benefits = t.raw(`tiers.${m.slug}.benefits`) as string[];

          return (
            <article
              key={m.id}
              ref={(el) => { cards.current[i] = el; }}
              className="relative min-w-[85%] shrink-0 snap-center overflow-hidden rounded-3xl transition-all duration-500"
              style={{
                background: s.bg,
                boxShadow: isActive ? s.glow : "0 4px 24px rgba(0,0,0,0.3)",
                opacity: isActive ? 1 : 0.72,
                transform: isActive ? "scale(1)" : "scale(0.97)",
              }}
            >
              {/* Decorative orbs */}
              <div
                className="pointer-events-none absolute -right-10 -top-10 size-44 rounded-full blur-3xl"
                style={{ backgroundColor: s.orb, opacity: 0.25 }}
                aria-hidden
              />
              <div
                className="pointer-events-none absolute -bottom-10 -left-10 size-36 rounded-full blur-3xl"
                style={{ backgroundColor: s.orb, opacity: 0.1 }}
                aria-hidden
              />

              <div className="relative p-6">
                {/* Top: badge + crest */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span
                      className="inline-block rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest"
                      style={{ background: s.badgeBg, color: s.accent }}
                    >
                      {t(`tiers.${m.slug}.name`)}
                    </span>
                    <p className="mt-3 font-headline text-headline-md leading-snug" style={{ color: s.text }}>
                      {t(`tiers.${m.slug}.tagline`)}
                    </p>
                  </div>
                  <Image
                    src={AppAssets.clubLogo}
                    alt="Borneo FC"
                    width={52}
                    height={52}
                    className="shrink-0 object-contain drop-shadow-lg"
                    style={{ opacity: isActive ? 0.9 : 0.55 }}
                  />
                </div>

                {/* Benefits */}
                <ul className="mt-5 space-y-2.5">
                  {benefits.map((benefit) => (
                    <li key={benefit} className="flex items-start gap-2.5">
                      <CheckCircle2
                        className="mt-0.5 size-4 shrink-0"
                        style={{ color: s.accent }}
                        aria-hidden
                      />
                      <span className="text-body-sm" style={{ color: "rgba(255,255,255,0.78)" }}>
                        {benefit}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Price */}
                <div
                  className="mt-5 pt-4"
                  style={{ borderTop: `1px solid ${s.priceDivider}` }}
                >
                  <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.4)" }}>
                    Harga per musim
                  </p>
                  <p className="mt-0.5 font-headline text-stat-lg" style={{ color: s.text }}>
                    {formatRupiah(m.pricePerSeason)}
                  </p>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {/* Dot indicators */}
      <div className="flex justify-center gap-2" aria-hidden>
        {tiers.map((m, i) => (
          <span
            key={m.id}
            className="h-1.5 rounded-full transition-all duration-300"
            style={{
              width: i === active ? "24px" : "6px",
              backgroundColor: i === active
                ? getTierStyle(tiers[i]).accent
                : "rgba(255,255,255,0.2)",
            }}
          />
        ))}
      </div>

      {/* CTA */}
      {currentMembership?.status === "pending" ? null
        : currentMembership?.status === "active" && currentMembership.enrollment_step === "complete" ? (
          <div className="flex w-full items-center justify-center gap-2 rounded-full py-3.5 font-headline text-headline-md font-bold text-white/50 bg-neutral-300 cursor-not-allowed">
            {isCurrentTier ? "Tier Aktif Saat Ini" : "Sudah Memiliki Membership"}
          </div>
        ) : isDisabled ? (
          <div className="flex w-full items-center justify-center gap-2 rounded-full py-3.5 font-headline text-headline-md font-bold text-white/50 bg-neutral-300 cursor-not-allowed">
            {isCurrentTier ? "Tier Aktif Saat Ini" : "Tidak Tersedia (Downgrade)"}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              const checkoutUrl = `/membership/checkout?tier=${tier.id}`;
              if (!isAuthenticated) {
                router.push(`/login?returnTo=${encodeURIComponent(checkoutUrl)}`);
              } else if (!hasPhone) {
                router.push(`/profil/verifikasi-hp?returnTo=${encodeURIComponent(checkoutUrl)}`);
              } else {
                router.push(checkoutUrl);
              }
            }}
            className="kinetic-gradient flex w-full items-center justify-center gap-2 rounded-full py-3.5 font-headline text-headline-md font-bold text-white transition-all duration-300 active:scale-95"
            style={{
              boxShadow: `0 4px 20px rgba(241,102,6,0.4), 0 0 0 1px rgba(241,102,6,0.15)`,
            }}
          >
            {t("pilihTier", { tier: t(`tiers.${tier.slug}.name`) })}
            <ArrowRight className="size-5" aria-hidden />
          </button>
        )}
    </>
  );
}
